import { supabase } from './supabase';

// Por ahora, usamos valores predeterminados para la configuración del catálogo
export function getDefaultCatalogSettings() {
  return {
    isOnline: true, 
    showPrices: true,
    showQuantities: true
  };
}

// Función para obtener la configuración del catálogo
export async function getCatalogSettings(businessId) {
  try {
    // Consultar la columna catalog_settings que sabemos que existe en businesses
    let { data, error } = await supabase
      .from('businesses')
      .select('catalog_settings')
      .eq('id', businessId)
      .single();
      
    if (error) {
      console.error('Error al obtener catalog_settings:', error);
      return getDefaultCatalogSettings();
    }
    
    if (data && data.catalog_settings) {
      return data.catalog_settings;
    }
    
    return getDefaultCatalogSettings();
    
  } catch (error) {
    console.error('Error al cargar configuración del catálogo:', error);
    return getDefaultCatalogSettings();
  }
}

export async function getPublicLots(businessId) {
  try {
    // Verificar que el negocio existe y obtener su configuración
    const { data: business, error: businessError } = await supabase
      .from('businesses')
      .select('id, name, description, catalog_settings')
      .eq('id', businessId)
      .single();
    
    if (businessError) {
      console.error('Error al obtener negocio:', businessError);
      return {
        business: { name: 'Catálogo' },
        lots: [],
        settings: getDefaultCatalogSettings()
      };
    }
    
    // Obtener la configuración del catálogo
    const catalogSettings = business.catalog_settings || await getCatalogSettings(businessId);
    
    // Si el catálogo no está en línea, devolver un objeto vacío
    if (!catalogSettings.isOnline) {
      return {
        business,
        lots: [],
        settings: catalogSettings
      };
    }
    
    // Obtener productos públicos
    const { data, error } = await supabase
      .from('lots')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_public', true)
      .gt('remaining_quantity', 0)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching lots:', error);
      return {
        business,
        lots: [],
        settings: catalogSettings
      };
    }
    
    // Transformar los datos para el formato que espera la UI
    return {
      business,
      lots: data.map(lot => ({
        id: lot.id,
        display_id: lot.display_id,
        title: lot.title || lot.name,
        quantity: lot.remaining_quantity,
        unit: lot.unit_sign,
        unitPrice: lot.unit_price,
        image: lot.image_url
      })),
      settings: catalogSettings
    };
  } catch (error) {
    console.error('Error al obtener lotes públicos:', error);
    return {
      business: { name: 'Catálogo' },
      lots: [],
      settings: getDefaultCatalogSettings()
    };
  }
}

// Nueva función para obtener lotes públicos usando slug de negocio
export async function getPublicLotsBySlug(businessSlug) {
  try {
    console.log('============= DIAGNÓSTICO LOTES POR SLUG =============');
    console.log('Buscando negocio con slug:', businessSlug);
    
    // Generamos un slug limpio
    const cleanSlug = businessSlug.toLowerCase().replace(/[^a-z0-9-]/g, '');
    console.log('Slug limpio para búsqueda:', cleanSlug);
    
    // Ver todos los slugs disponibles para diagnóstico
    const { data: allSlugs } = await supabase
      .from('businesses')
      .select('id, name, slug');
      
    if (allSlugs && allSlugs.length > 0) {
      console.log('Slugs disponibles en la base de datos:');
      allSlugs.forEach(b => {
        const nameSlug = generateSlugFromName(b.name);
        console.log(`ID: ${b.id}, Nombre: ${b.name}, Slug DB: ${b.slug || 'null'}, Slug calculado: ${nameSlug}`);
      });
    }
    
    // Primero, intentamos buscar el negocio con el slug exacto
    let { data: business, error: businessError } = await supabase
      .from('businesses')
      .select('id, name, description, catalog_settings, slug')
      .eq('slug', cleanSlug)
      .single();
    
    // Si no encontramos por slug exacto, intentamos con comparación manual
    if (businessError) {
      console.log('Slug exacto no encontrado, intentando comparación manual...');
      
      // Obtener todos los negocios
      const { data: allBusinesses, error: allBusinessesError } = await supabase
        .from('businesses')
        .select('id, name, description, catalog_settings, slug');
      
      if (allBusinessesError) {
        console.error('Error al obtener todos los negocios:', allBusinessesError);
        throw allBusinessesError;
      }
      
      console.log(`Comparando con ${allBusinesses.length} negocios...`);
      
      // Encontrar el negocio cuyo nombre convertido a slug coincida
      business = allBusinesses.find(b => {
        // Generar slug del nombre
        const nameSlug = generateSlugFromName(b.name);
        
        console.log(`Comparando: "${nameSlug}" con "${cleanSlug}" para negocio "${b.name}"`);
        
        // Intentar coincidir por cualquiera de los métodos
        return nameSlug === cleanSlug || (b.slug && b.slug.toLowerCase() === cleanSlug);
      });
    }
    
    if (!business) {
      console.error('No se encontró ningún negocio con slug o nombre que coincida con:', cleanSlug);
      return {
        business: { name: 'Catálogo' },
        lots: [],
        settings: getDefaultCatalogSettings()
      };
    }
    
    console.log('Negocio encontrado:', business.name, 'ID:', business.id);
    
    // Ahora que tenemos el ID del negocio, obtener los lotes públicos
    return getPublicLots(business.id);
  } catch (error) {
    console.error('Error al obtener lotes públicos por slug:', error);
    return {
      business: { name: 'Catálogo' },
      lots: [],
      settings: getDefaultCatalogSettings()
    };
  }
}

// Función auxiliar para generar slug desde nombre de manera consistente
function generateSlugFromName(name) {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/['´`]/g, '')  // Eliminar apóstrofes y acentos
    .replace(/[^a-z0-9]+/g, '-')  // Reemplazar caracteres no alfanuméricos por guiones
    .replace(/(^-|-$)/g, '');  // Eliminar guiones al inicio y final
} 